import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  dateToSecondsSinceMidnight,
  secondsSinceMidnightToDate,
} from 'shared/dates';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';

type TimeFieldProps = {
  name: string;
  value: number | null;
  error?: string;
};

const useStyles = makeStyles()((theme) => ({
  clearDateButton: {
    // Make "clear" button secondary/grey
    '& button:first-of-type': {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.grey[300],
      ...theme.applyStyles('dark', {
        backgroundColor: theme.palette.grey[700],
      }),
      '&:hover': {
        backgroundColor: theme.palette.grey[500],
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.grey[600],
        }),
      },
    },
  } as CSSObject,
}));

export const TimeField = (props: TimeFieldProps): JSX.Element => {
  const { name, value, error } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { setValue, setError, clearErrors } = useFormContext();

  return (
    <TimePicker
      value={secondsSinceMidnightToDate(value)}
      timezone="UTC"
      onChange={(newValue) =>
        setValue(name, dateToSecondsSinceMidnight(newValue), {
          shouldValidate: true,
        })
      }
      ampm
      slotProps={{
        textField: {
          id: `field-control-${name}`,
          helperText: error,
          error: Boolean(error),
          fullWidth: true,
          // @ts-expect-error
          'data-testid': `field-control-${name}`,
        },
        actionBar: {
          actions: ['clear', 'accept'],
          classes: { root: classes.clearDateButton },
        },
      }}
      format="hh:mm a"
      onError={(errMsg) => {
        if (errMsg) {
          setError(name, { message: t('formHours.errors.invalid') });
        } else {
          clearErrors(name);
        }
      }}
    />
  );
};
